<!-- Responsibility: Implement talk dialog behavior for the ui/components/dialog surface.
Scope: Owns ui/components/dialog concerns only; cross-surface orchestration is excluded. -->
<talk-dialog>
  <div class="talk-body">
    <p class="fm-desc">{ state.prompt }</p>

    <input
      ref="input"
      class="talk-input"
      type="text"
      value={ state.message }
      placeholder={ state.placeholder }
      oninput={ onInput }
      onkeydown={ onKeydown }
      autocomplete="off"
    >
  </div>

  <script>
    import * as gameStore from '../../../game-store.js'
    import { playEffect } from '../../../engine/audio.js'
    import { buildActionTarget, buildToolRef } from '../../helpers/action-refs.ts'

    export default {
      state: {
        message: '',
        openToken: 0,
        prompt: 'What do you want to say?',
        placeholder: 'Say something...',
        baseInput: 'Talk',
        targetName: '',
        targetType: '',
      },

      onMounted() {
        this.syncState(this.props)
        this.update()
        requestAnimationFrame(() => this.$('input[ref="input"]')?.focus())
      },

      onBeforeUpdate(newProps) {
        const nextToken = Number(newProps?.openToken ?? 0)
        if (nextToken !== Number(this.state.openToken ?? 0)) {
          this.state.message = ''
          this.state.openToken = nextToken
          requestAnimationFrame(() => this.$('input[ref="input"]')?.focus())
        }
        this.syncState(newProps)
      },

      syncState(props) {
        const data = props.content ?? {}
        const actionContext = data.actionContext && typeof data.actionContext === 'object' ? data.actionContext : null
        const actionTarget = Array.isArray(actionContext?.explicitTargets) ? actionContext.explicitTargets[0] : null
        const targetName = String(data.targetName ?? actionContext?.targetName ?? actionTarget?.label ?? '').trim()
        
        this.state.prompt = String(data.prompt ?? 'What do you want to say?')
        this.state.placeholder = data.placeholder ? String(data.placeholder) : (targetName ? `Say something to ${targetName}...` : 'Say something...')
        this.state.targetName = targetName
        this.state.targetType = String(data.targetType ?? actionContext?.targetType ?? actionTarget?.kind ?? '').trim()
        
        const rawBase = String(data.baseInput ?? '').trim()
        this.state.baseInput = rawBase || (targetName ? `Talk to ${targetName}` : 'Talk')
      },

      onInput(e) {
        this.update({ message: e.target.value })
      },

      onKeydown(e) {
        if (e.key === 'Enter') {
          e.preventDefault()
          this.confirm()
        }
      },

      async confirm() {
        const text = this.state.message.trim()
        if (!text) return
        playEffect('ui_click')
        await this.props.requestModalClose?.('submit')
        const input = `${this.state.baseInput}: "${text.replace(/"/g, "'")}"`
        const targetType = this.state.targetType === 'npc' || this.state.targetType === 'companion'
          ? this.state.targetType
          : ''
        const actionContext = this.props.content?.actionContext && typeof this.props.content.actionContext === 'object'
          ? this.props.content.actionContext
          : {}
        const explicitTargets = targetType && this.state.targetName
          ? [buildActionTarget(targetType, this.state.targetName)]
          : (Array.isArray(actionContext?.explicitTargets) ? actionContext.explicitTargets : [])
        gameStore.handleAction(input, undefined, {
          source: 'player',
          mode: actionContext?.mode ?? 'do',
          category: actionContext?.category ?? 'interact',
          tool: actionContext?.tool ?? buildToolRef('free-text', 'talk', this.state.targetName ? `Talk to ${this.state.targetName}` : 'Talk'),
          explicitTargets,
          tags: [
            ...new Set([
              ...((Array.isArray(actionContext?.tags) ? actionContext.tags : []).filter(Boolean)),
              'talk_dialog',
              'dialog_free_text',
            ]),
          ],
        })
      },
    }
  </script>

  <style>
    talk-dialog {
      display: block;
    }

    .talk-body {
      padding-top: var(--space-xs);
    }

    .talk-input {
      width: 100%;
      background: var(--bg);
      border: 1px solid var(--border);
      color: var(--text-prose);
      padding: var(--space-sm);
      font-family: var(--font-system);
      font-size: var(--space-md);
      border-radius: 0.25rem;
      outline: none;
      margin-top: var(--space-sm);
    }

    .talk-input:focus {
      border-color: var(--gold);
    }
  </style>
</talk-dialog>
